class NBPersonalize extends HTMLElement {
  constructor() {
    super();

    const form = this.querySelector('form');
    const personalizeText = this.querySelector('[js-personalize-text]');
    const personalizeTextContainer = this.querySelectorAll('[js-personalize-text-container]');
    const fontRadios = this.querySelectorAll('[js-personalize-font]');
    const colorRadios = this.querySelectorAll('[js-personalize-color]');
    const heading = this.querySelector('[js-personalize-heading]');
    const maxLabel = this.querySelector('[js-personalize-max-label]');
    const defaultFont = this.dataset.font;
    const defaultColor = this.dataset.color;

    // Variant-level config overrides (see snippets/nb-personalize.liquid)
    let variantConfig = { default: {}, variants: {} };
    const variantConfigEl = this.querySelector('[js-personalize-variant-config]');
    if (variantConfigEl) {
      try {
        variantConfig = JSON.parse(variantConfigEl.textContent);
      } catch (e) {
        console.warn('nb-personalize: could not parse variant config', e);
      }
    }

    // Track the last applied state (what should be submitted to cart)
    let appliedText = '';
    let appliedFontValue = '';
    let appliedColorValue = '';
    let hasApplied = false;
    let justApplied = false;

    // Get a reference to our parent modal BEFORE it gets moved to document.body
    const modal = this.closest('modal-dialog');

    // Restore applied values into the form when modal opens
    const modalOpener = document.querySelector(`#PopupOpener-${this.dataset.block}`);
    if (modalOpener) {
      const openerButton = modalOpener.querySelector('button');
      if (openerButton) {
        openerButton.addEventListener('click', () => {
          if (hasApplied) {
            personalizeText.value = appliedText;
            personalizeTextContainer.forEach((container) => {
              container.innerHTML = appliedText;
            });
            fontRadios.forEach((radio) => {
              radio.checked = radio.value === appliedFontValue;
            });
            colorRadios.forEach((radio) => {
              radio.checked = radio.value === appliedColorValue;
            });
            this.dataset.font = appliedFontValue || defaultFont;
            this.dataset.color = appliedColorValue || defaultColor;
          }
        });
      }
    }

    // Apply Design button — save values and keep them in the form fields
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      justApplied = true;

      if (modal) modal.hide();

      const etaInput = document.querySelector(`#Estimated-Delivery-Input-${this.dataset.block}`);

      if (personalizeText.value.length > 0) {
        hasApplied = true;
        appliedText = personalizeText.value;
        const checkedFont = this.querySelector('[js-personalize-font]:checked');
        appliedFontValue = checkedFont ? checkedFont.value : '';
        const checkedColor = this.querySelector('[js-personalize-color]:checked');
        appliedColorValue = checkedColor ? checkedColor.value : '';

        const opener = document.querySelector(`#PopupOpener-${this.dataset.block}`);
        if (opener) opener.dataset.hasPersonalization = 'true';

        if (etaInput && etaInput.dataset.personalizationEta) {
          etaInput.value = etaInput.dataset.personalizationEta;
        }
      } else {
        hasApplied = false;
        appliedText = '';
        appliedFontValue = '';
        appliedColorValue = '';

        const opener = document.querySelector(`#PopupOpener-${this.dataset.block}`);
        if (opener) opener.dataset.hasPersonalization = 'false';

        if (etaInput && etaInput.dataset.standardEta) {
          etaInput.value = etaInput.dataset.standardEta;
        }
      }
    });

    // Watch our specific modal for the 'open' attribute being removed
    // This is more reliable than the global modalClosed event
    if (modal) {
      const observer = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          if (mutation.attributeName === 'open' && !modal.hasAttribute('open')) {

            if (justApplied) {
              justApplied = false;
              return;
            }

            if (hasApplied) {
              // Restore to last applied values
              personalizeText.value = appliedText;
              fontRadios.forEach((radio) => {
                radio.checked = radio.value === appliedFontValue;
              });
              colorRadios.forEach((radio) => {
                radio.checked = radio.value === appliedColorValue;
              });
              this.dataset.font = appliedFontValue || defaultFont;
              this.dataset.color = appliedColorValue || defaultColor;
            } else {
              // Never applied — clear text and reset radios to first option
              // (mirrors initial DOM state so text-without-font can't happen)
              personalizeText.value = '';
              fontRadios.forEach((radio, i) => (radio.checked = i === 0));
              colorRadios.forEach((radio, i) => (radio.checked = i === 0));
              personalizeTextContainer.forEach((container) => {
                container.innerHTML = '';
              });
              this.dataset.font = defaultFont;
              this.dataset.color = defaultColor;

              const opener = document.querySelector(`#PopupOpener-${this.dataset.block}`);
              if (opener) opener.dataset.hasPersonalization = 'false';

              const etaInput = document.querySelector(`#Estimated-Delivery-Input-${this.dataset.block}`);
              if (etaInput && etaInput.dataset.standardEta) {
                etaInput.value = etaInput.dataset.standardEta;
              }
            }

            resetHiddenSelections();
          }
        }
      });
      observer.observe(modal, { attributes: true, attributeFilter: ['open'] });
    }

    // Live preview as user types — only letters and spaces allowed
    personalizeText.addEventListener('input', () => {
      const sanitized = personalizeText.value.replace(/[^a-zA-Z ]/g, '');
      if (sanitized !== personalizeText.value) {
        const caret = Math.max(0, (personalizeText.selectionStart || 0) - (personalizeText.value.length - sanitized.length));
        personalizeText.value = sanitized;
        personalizeText.setSelectionRange(caret, caret);
      }

      personalizeTextContainer.forEach((container) => {
        container.innerHTML = personalizeText.value;

        if (container.tagName === 'tspan') {
          const svg = container.closest('svg');
          if (svg) {
            svg.style.display = 'none';
            svg.offsetHeight;
            svg.style.display = '';
          }

          const originalFilter = container.getAttribute('filter');
          container.removeAttribute('filter');
          setTimeout(() => {
            container.setAttribute('filter', originalFilter);
          }, 0);
        }
      });
    });

    fontRadios.forEach((radio) => {
      radio.addEventListener('change', () => {
        this.dataset.font = radio.value;
      });
    });

    colorRadios.forEach((radio) => {
      radio.addEventListener('change', () => {
        this.dataset.color = radio.value;
      });
    });

    // Merge the current variant's overrides over the product defaults and apply
    // heading, max characters, field name and price to the DOM.
    const applyVariantConfig = () => {
      const variantId = document.body.dataset.variant;
      const overrides = (variantId && variantConfig.variants && variantConfig.variants[variantId]) || {};
      const cfg = Object.assign({}, variantConfig.default || {});
      Object.keys(overrides).forEach((key) => {
        if (overrides[key] !== null && overrides[key] !== undefined) cfg[key] = overrides[key];
      });

      if (heading && cfg.heading != null) heading.textContent = cfg.heading;

      const max = parseInt(cfg.maxCharacters, 10);
      if (!isNaN(max) && max > 0) {
        personalizeText.maxLength = max;
        if (maxLabel) maxLabel.textContent = `Text (max. ${max} character${max !== 1 ? 's' : ''})`;
        if (personalizeText.value.length > max) {
          personalizeText.value = personalizeText.value.slice(0, max);
          personalizeText.dispatchEvent(new Event('input'));
        }
      }

      if (cfg.fieldName) personalizeText.name = cfg.fieldName;

      const hasPrice = cfg.price !== null && cfg.price !== undefined && cfg.price !== '';
      [this, modalOpener].filter(Boolean).forEach((scope) => {
        scope.querySelectorAll('[js-personalize-price]').forEach((el) => (el.textContent = hasPrice ? cfg.price : ''));
        scope.querySelectorAll('[js-personalize-price-on]').forEach((el) => (el.hidden = !hasPrice));
        scope.querySelectorAll('[js-personalize-price-off]').forEach((el) => (el.hidden = hasPrice));
      });
    };

    // If the checked font/color is hidden for the current variant (excluded colors or a
    // variant-level font/color list), move the selection to the first visible option.
    const resetHiddenSelections = () => {
      [
        { radios: fontRadios, attr: 'font' },
        { radios: colorRadios, attr: 'color' },
      ].forEach(({ radios, attr }) => {
        if (!radios.length) return;
        const checked = Array.from(radios).find((r) => r.checked);
        const checkedLabel = checked && checked.nextElementSibling;
        if (checked && checkedLabel && getComputedStyle(checkedLabel).display !== 'none') return;

        for (const radio of radios) {
          const label = radio.nextElementSibling;
          if (label && getComputedStyle(label).display !== 'none') {
            radio.checked = true;
            this.dataset[attr] = radio.value;
            if (hasApplied) {
              if (attr === 'font') appliedFontValue = radio.value;
              else appliedColorValue = radio.value;
            }
            break;
          }
        }
      });
    };

    const onVariantChange = () => {
      applyVariantConfig();
      resetHiddenSelections();
    };

    onVariantChange();
    const bodyObserver = new MutationObserver(onVariantChange);
    bodyObserver.observe(document.body, { attributes: true, attributeFilter: ['data-variant'] });
  }
}
if (!customElements.get('nb-personalize')) {
  customElements.define('nb-personalize', NBPersonalize);
}
